import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import { useCreateMoveJob } from "../../data/truckingData";
import { useQuery } from "@tanstack/react-query";
import { apiClient, getApiErrorMessage } from "../../api/client";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NewMoveJobDialog = ({ open, onOpenChange }: Props) => {
  const createMoveJob = useCreateMoveJob();

  const { data: customers = [] } = useQuery({
    queryKey: ['customers-list'],
    queryFn: () => apiClient.get('/customers').then(r => r.data?.data ?? r.data ?? []),
    staleTime: 60_000,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['team-users'],
    queryFn: () => apiClient.get('/team').then(r => r.data?.data ?? r.data ?? []),
    staleTime: 60_000,
  });

  const [form, setForm] = useState({
    name: "",
    operator_id: "",
    rig_number: "",
    origin_lsd: "",
    destination_lsd: "",
    planned_start: "",
    load_sequence_required: false,
    coordinator_id: "",
    notes: "",
  });

  const update = (key: string, value: string | boolean) =>
    setForm((p) => ({ ...p, [key]: value }));

  const reset = () =>
    setForm({
      name: "",
      operator_id: "",
      rig_number: "",
      origin_lsd: "",
      destination_lsd: "",
      planned_start: "",
      load_sequence_required: false,
      coordinator_id: "",
      notes: "",
    });

  /**
   * POST /trucking/move-jobs reads camelCase (`operatorId`, `rigNumber`,
   * `originLsd`, `destinationLsd`, `plannedStart`, `loadSequenceRequired`,
   * `coordinatorId`, `notes`). This sent the form's snake_case keys, so the
   * route saved the name and notes and dropped everything else while
   * answering 201 (#815). Blank fields are left out.
   */
  const handleSubmit = async () => {
    if (!form.name.trim() || !form.origin_lsd.trim() || !form.destination_lsd.trim()) {
      toast.error("Name, origin, and destination are required");
      return;
    }
    const body: Record<string, unknown> = {
      name: form.name.trim(),
      originLsd: form.origin_lsd.trim(),
      destinationLsd: form.destination_lsd.trim(),
      loadSequenceRequired: form.load_sequence_required,
    };
    if (form.operator_id) body.operatorId = form.operator_id;
    if (form.rig_number.trim()) body.rigNumber = form.rig_number.trim();
    if (form.planned_start) body.plannedStart = form.planned_start;
    if (form.coordinator_id) body.coordinatorId = form.coordinator_id;
    if (form.notes.trim()) body.notes = form.notes.trim();
    try {
      await createMoveJob.mutateAsync(body);
      toast.success("Move job created");
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to create move job"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-card border-border">
        <DialogHeader>
          <DialogTitle className="text-foreground">New Move Job</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs text-muted-foreground">Job Name</Label>
            <Input
              className="mt-1 bg-secondary/50 border-border"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="e.g. Rig 14 — Pad 6-22 to Pad 11-34"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Operator</Label>
              <Select value={form.operator_id} onValueChange={(v) => update("operator_id", v)}>
                <SelectTrigger className="mt-1 bg-secondary/50 border-border">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Rig Number</Label>
              <Input
                className="mt-1 bg-secondary/50 border-border"
                value={form.rig_number}
                onChange={(e) => update("rig_number", e.target.value)}
                placeholder="e.g. Rig 14"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Origin LSD</Label>
              <Input
                className="mt-1 bg-secondary/50 border-border"
                value={form.origin_lsd}
                onChange={(e) => update("origin_lsd", e.target.value)}
                placeholder="e.g. 06-22-048-07W5"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Destination LSD</Label>
              <Input
                className="mt-1 bg-secondary/50 border-border"
                value={form.destination_lsd}
                onChange={(e) => update("destination_lsd", e.target.value)}
                placeholder="e.g. 11-34-048-07W5"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Planned Start</Label>
              <Input
                type="date"
                className="mt-1 bg-secondary/50 border-border"
                value={form.planned_start}
                onChange={(e) => update("planned_start", e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Coordinator</Label>
              <Select value={form.coordinator_id} onValueChange={(v) => update("coordinator_id", v)}>
                <SelectTrigger className="mt-1 bg-secondary/50 border-border">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u: any) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between py-1">
            <Label className="text-xs text-muted-foreground">Load Sequence Required</Label>
            <Switch
              checked={form.load_sequence_required}
              onCheckedChange={(v) => update("load_sequence_required", v)}
            />
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">Notes</Label>
            <Textarea
              className="mt-1 bg-secondary/50 border-border resize-none"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              rows={2}
              placeholder="Additional details..."
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={handleSubmit}
              disabled={createMoveJob.isPending}
            >
              {createMoveJob.isPending ? "Creating..." : "Create Move Job"}
            </Button>
            <Button variant="outline" className="border-border" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewMoveJobDialog;
